import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';

import type { MediaLibraryItem } from '../media.api';
import { MediaCard } from './MediaCard';

export interface MediaGridProps {
  items: MediaLibraryItem[];
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (id: string) => void;
  onDownload: (item: MediaLibraryItem) => void;
  /** Welke bestanden op dit moment worden opgehaald — per bestand, niet één vlag voor alles. */
  downloadingIds?: ReadonlySet<string>;
  loadWebImage?: (item: MediaLibraryItem) => Promise<string | null>;
  productHref?: (item: MediaLibraryItem) => string | undefined;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Tekent dit onderdeel zelf de tegels? Bij de lijstweergave niet — dan komt de lijst als kind. */
  renderItems?: boolean;
  children?: ReactNode;
}

/** The DAM library grid + pager (h11-1, mockup dam.html `.mgrid` + `.pager`). */
export function MediaGrid({ items, selectedIds, onToggleSelect, onDownload, downloadingIds, loadWebImage, productHref, total, page, pageSize, onPageChange, renderItems = true, children }: MediaGridProps) {
  const { t } = useTranslation();
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="space-y-4">
      {children}
      {renderItems && (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" data-testid="media-grid">
        {items.map((item) => (
          <MediaCard
            isDownloading={downloadingIds?.has(item.id) ?? false}
            item={item}
            loadWebImage={loadWebImage}
            key={item.id}
            onDownload={onDownload}
            onToggleSelect={onToggleSelect}
            productHref={productHref?.(item)}
            selected={selectedIds.has(item.id)}
          />
        ))}
      </div>
      )}

      <div className="flex flex-wrap items-center gap-3" data-testid="media-pager">
        <Button disabled={page <= 1} onClick={() => onPageChange(page - 1)} size="sm" variant="outline">
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">chevron_left</span>
          {t('media.library.pager.previous')}
        </Button>
        <Button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} size="sm" variant="outline">
          {t('media.library.pager.next')}
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">chevron_right</span>
        </Button>
        <span className="ml-auto text-xs text-[var(--foreground-muted)]">
          {t('media.library.pager.range', { rangeStart, rangeEnd, total })}
        </span>
      </div>
    </div>
  );
}
